import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Avatar,
  Box,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  createOrganization,
  uploadOrganizationLogo,
} from "@/api/organizationsApi";
import type { OrganizationCreate } from "@/api/organizationsApi";
import { useOrganization } from "@/contexts/OrganizationContext";

interface CreateOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateOrganizationDialog({
  open,
  onClose,
}: CreateOrganizationDialogProps) {
  const { setCurrentOrganization, refreshOrganizations } = useOrganization();
  const [formData, setFormData] = useState<OrganizationCreate>({ name: "" });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setLogoFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleCreate = async () => {
    try {
      setSubmitting(true);
      setError(null);
      let newOrg = await createOrganization(formData);

      if (logoFile) {
        newOrg = await uploadOrganizationLogo(newOrg.id, logoFile);
      }

      await refreshOrganizations();
      setCurrentOrganization(newOrg);
      handleClose();
    } catch (err) {
      console.error(err);
      setError("Failed to create organization");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "" });
    setLogoFile(null);
    setPreviewUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create New Organization
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Organization Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Address"
            value={formData.address || ""}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone || ""}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            margin="normal"
          />
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Button variant="outlined" component="label">
              Upload Logo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleLogoChange}
              />
            </Button>
            {previewUrl && (
              <Avatar src={previewUrl} sx={{ width: 56, height: 56 }} />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!formData.name || submitting}
        >
          {submitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
